import { useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel
} from '@/components/ui/alert-dialog'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Unplug, AlertCircle } from 'lucide-react'

/**
 * XeroDisconnectButton Component
 *
 * Handles disconnecting from Xero with confirmation dialog.
 * Shows error states and provides support contact on failure.
 *
 * @param {Object} props
 * @param {Function} props.onDisconnect - Async callback function to handle disconnect
 * @param {boolean} props.disabled - Whether button should be disabled
 */
export default function XeroDisconnectButton({ onDisconnect, disabled = false }) {
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)
  const [isDisconnecting, setIsDisconnecting] = useState(false)
  const [error, setError] = useState(null)

  const handleDisconnectClick = () => {
    setError(null)
    setShowConfirmDialog(true)
  }

  const handleConfirmDisconnect = async () => {
    setIsDisconnecting(true)
    setError(null)

    try {
      await onDisconnect()
      setShowConfirmDialog(false)
    } catch (err) {
      console.error('[XeroDisconnect] Error:', err)
      setError(err.message || 'Failed to disconnect from Xero')
    } finally {
      setIsDisconnecting(false)
    }
  }

  const handleCancelDisconnect = () => {
    setShowConfirmDialog(false)
    setError(null)
  }

  return (
    <>
      <Button
        variant="outline"
        onClick={handleDisconnectClick}
        disabled={disabled || isDisconnecting}
        className="text-red-600 hover:text-red-700 hover:bg-red-50"
      >
        <Unplug className="w-4 h-4 mr-2" />
        {isDisconnecting ? 'Disconnecting...' : 'Disconnect from Xero'}
      </Button>

      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Disconnect from Xero?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove your Xero connection and stop automatic data syncing.
              You can reconnect at any time.
            </AlertDialogDescription>
          </AlertDialogHeader>

          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                {error}
                {error.includes('Failed') && (
                  <>
                    {' '}
                    Please try again or contact support at{' '}
                    <a href="mailto:support@caledoniamushrooms.com" className="underline">
                      support@caledoniamushrooms.com
                    </a>
                  </>
                )}
              </AlertDescription>
            </Alert>
          )}

          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={handleCancelDisconnect}
              disabled={isDisconnecting}
            >
              Cancel
            </AlertDialogCancel>
            <Button
              onClick={handleConfirmDisconnect}
              disabled={isDisconnecting}
              variant="destructive"
            >
              {isDisconnecting ? 'Disconnecting...' : 'Disconnect'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
